import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { QRCodeSVG } from 'qrcode.react';
import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  SAMLAuthProvider,
  TotpMultiFactorGenerator,
  TotpSecret,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { auth } from '../../../api/firebase/config';
import {
  generateMfaBackupCodes,
  prepareMfaEnrollment,
} from '../../../api/firebase/function/security/securityMfaFunction';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { MFA_TOTP_CODE_LENGTH, SSO_REAUTH_LABEL } from '../../../constants/enum';
import {
  gray5,
  gray40,
  gray60,
  gray100,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularSm, regularXs } from '../../../constants/location';
import { resolveSamlReauthProviderId } from '../../../utils/samlUser';
import CustomTextfield from '../form/CustomTextfield';
import MfaBackupCodesView from './MfaBackupCodesView';

/** 表示名（GCIP の enrollment displayName。画面文言は「認証アプリ」で統一） */
const FACTOR_DISPLAY_NAME = '認証アプリ';

type Step = 'reauth' | 'verify' | 'backup';

interface Props {
  open: boolean;
  /** 強制ゲートから開く場合 true（閉じる導線を出さない） */
  disableClose?: boolean;
  onClose: () => void;
  /** 有効化 + バックアップコード保存まで完了したとき */
  onEnrolled: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '520px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  bodyText: {
    ...dialogText,
    marginBottom: '16px',
  },
  caption: {
    ...regularXs,
    color: gray60,
    marginTop: '8px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    marginBottom: '16px',
    padding: '8px 12px',
  },
  qrWrapper: {
    backgroundColor: white,
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  secretBox: {
    ...regularSm,
    backgroundColor: gray5,
    borderRadius: '4px',
    color: gray100,
    fontFamily: 'monospace',
    marginBottom: '16px',
    overflowWrap: 'anywhere',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
};

/**
 * 認証アプリ（TOTP）有効化ダイアログ（#2984・PRD §5-3）。
 * フロー: （必要時のみ再認証）→ QR 表示 → 6桁コード検証で有効化
 * → バックアップコード表示（コピー / PDF ダウンロードのいずれか必須）→ 完了。
 * 再認証は通常パスワードだが、SAML SSO 専用ユーザーは IdP のポップアップで行う（#3214）。
 */
const MfaEnrollDialog: React.FC<Props> = ({ open, disableClose, onClose, onEnrolled }) => {
  const [step, setStep] = useState<Step>('verify');
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupSaved, setBackupSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const email = auth.currentUser?.email ?? '';
  // SAML 再認証を使うユーザーか（パスワードを持たない SAML 専用ユーザーのみ・#3214）
  const samlProviderId = resolveSamlReauthProviderId(auth.currentUser);

  // シークレット生成（開いたとき）。最近ログインしていない場合は再認証ステップへ
  const startEnrollment = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      // GCIP は第二要素登録前にメール検証を要求する。Gen Drive の登録フローで
      // メール所有は確認済みのため、サーバー側で emailVerified を立ててから
      // トークンを更新して登録セッションを開始する
      await prepareMfaEnrollment();
      await user.getIdToken(true);
      const session = await multiFactor(user).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
      setSecret(totpSecret);
      setQrUrl(totpSecret.generateQrCodeUrl(email, 'Gen Drive'));
      setStep('verify');
    } catch (e) {
      if (e instanceof FirebaseError && e.code === 'auth/requires-recent-login') {
        setStep('reauth');
      } else {
        Sentry.captureException(e);
        setErrorMessage(
          e instanceof FirebaseError
            ? getErrorMessageFromAuthErrorCode(e.code)
            : '認証アプリの設定を開始できませんでした。',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setStep('verify');
      setSecret(null);
      setQrUrl('');
      setPassword('');
      setCode('');
      setBackupCodes([]);
      setBackupSaved(false);
      setErrorMessage('');
      void startEnrollment();
    }
  }, [open]);

  const handleReauth = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    // パスワード再認証はメールアドレスが必須（SAML 再認証では使わない）
    if (!samlProviderId && !email) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      // SAML 専用ユーザーはパスワードを持たないため IdP のポップアップで再認証する。
      // ポップアップはクリック直後に開く必要があるため、ここまでに await を挟まない（#3214）
      if (samlProviderId) {
        await reauthenticateWithPopup(user, new SAMLAuthProvider(samlProviderId));
      } else {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(email, password));
      }
      setPassword('');
      await startEnrollment();
    } catch (e) {
      setErrorMessage(
        e instanceof FirebaseError
          ? getErrorMessageFromAuthErrorCode(e.code)
          : '再認証に失敗しました。',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !secret) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
      await multiFactor(user).enroll(assertion, FACTOR_DISPLAY_NAME);
      const result = await generateMfaBackupCodes();
      setBackupCodes(result.codes);
      setStep('backup');
    } catch (e) {
      if (e instanceof FirebaseError) {
        setErrorMessage(
          e.code === 'auth/invalid-verification-code' ||
            e.code === 'auth/totp-challenge-timeout' ||
            e.code === 'auth/invalid-argument'
            ? '確認コードが正しくありません。認証アプリに表示されている最新のコードを入力してください。'
            : getErrorMessageFromAuthErrorCode(e.code),
        );
      } else {
        Sentry.captureException(e);
        setErrorMessage('認証アプリの設定に失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (submitting || disableClose) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>認証アプリの設定</Typography>
        {!disableClose && step !== 'backup' && (
          <IconButton onClick={handleClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}

      {step === 'reauth' && (
        <>
          <Typography sx={styles.bodyText}>
            {samlProviderId
              ? '安全のため、組織の ID プロバイダでもう一度ログインしてください。'
              : '安全のため、パスワードを再入力してください。'}
          </Typography>
          {/* SAML 専用ユーザーはパスワードを持たないため、パスワード欄は出さない（#3214） */}
          {!samlProviderId && (
            <CustomTextfield
              type="password"
              labelText="パスワード"
              inputWidth="100%"
              fontSizeIs14px
              value={password}
              onChange={(e): void => setPassword(e.target.value)}
              formMb="0"
            />
          )}
          <Box sx={styles.dialogActions}>
            <Button
              onClick={handleReauth}
              disabled={submitting || (!samlProviderId && !password)}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              {samlProviderId ? SSO_REAUTH_LABEL : '次へ'}
            </Button>
          </Box>
        </>
      )}

      {step === 'verify' && (
        <>
          <Typography sx={styles.bodyText}>
            Google Authenticator / Microsoft Authenticator / 1Password などの認証アプリで
            QRコードを読み取り、表示された{MFA_TOTP_CODE_LENGTH}桁のコードを入力してください。
          </Typography>
          {qrUrl ? (
            <Box sx={styles.qrWrapper}>
              <QRCodeSVG value={qrUrl} size={180} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {secret && (
            <>
              <Typography sx={styles.caption}>
                QRコードを読み取れない場合は、次のセットアップキーを認証アプリに手入力してください。
              </Typography>
              <Box sx={styles.secretBox}>{secret.secretKey}</Box>
            </>
          )}
          <CustomTextfield
            labelText={`確認コード（${MFA_TOTP_CODE_LENGTH}桁）`}
            inputWidth="100%"
            fontSizeIs14px
            placeholder="123456"
            value={code}
            onChange={(e): void => setCode(e.target.value)}
            formMb="0"
          />
          <Box sx={styles.dialogActions}>
            {!disableClose && (
              <Button
                onClick={handleClose}
                disabled={submitting}
                sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
              >
                キャンセル
              </Button>
            )}
            <Button
              onClick={handleVerify}
              disabled={submitting || code.trim().length !== MFA_TOTP_CODE_LENGTH || !secret}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              有効化する
            </Button>
          </Box>
        </>
      )}

      {step === 'backup' && (
        <>
          <Typography sx={styles.bodyText}>
            認証アプリを有効化しました。認証アプリを紛失した場合に備えて、
            以下のバックアップコードを必ず保存してください（各コードは1回のみ使用できます）。
          </Typography>
          <MfaBackupCodesView codes={backupCodes} email={email} onSavedChange={setBackupSaved} />
          <Typography sx={styles.caption}>
            ※ コピーまたはダウンロードすると「完了」を押せるようになります。
          </Typography>
          <Box sx={styles.dialogActions}>
            <Button
              onClick={(): void => {
                onEnrolled();
                onClose();
              }}
              disabled={!backupSaved}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              完了
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default MfaEnrollDialog;
